import { WorkflowForEntity } from "../workflow-for-entity/workflow-for-entity";
import { autoinject, transient, computedFrom, customElement, bindable } from "aurelia-framework";
import { WorkflowAssignmentModel, WorkflowStateModel } from "../../../models/workflow/workflow.models";
import { RightShipDialogServiceConstants } from "../../../constants/basics/rightship-dialog.constants";
import { NotificationType, NotificationClientService } from "../../../services/basics/notification-client.service";
import { WorkflowAssignmentsService } from "../../../services/workflow/workflow-assignments-service";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { EventAggregator } from "aurelia-event-aggregator";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { Claims } from "../../../constants/authentication/claims";
import { Messages } from "../../../configuration/event-messages.constants";
import { RightShipWorkflowStateEnum } from "../../../models/workflow/rightship.workflow.models";
import { RightShipOnboardingQuestionCommentService } from "../../../services/rightship-onboarding/rightship-onboarding-question-comment.service";

@autoinject()
@transient()
@customElement('rightship-workflow-for-entity')
export class RightshipWorkflowForEntity extends WorkflowForEntity {

    public isShowNextSteps: boolean;
    isCancelling: boolean;
    public canReopen: boolean;
    public canCancel: boolean;
    public canInvalidate: boolean;

    @bindable
    shouldShowCancel: boolean = true;

    @bindable
    shouldShowInvalidate: boolean = true;

    @bindable
    shouldShowReopen: boolean = true;

    signalFunc: any;

    @computedFrom("workflowState", "workflowState.state")
    get pendingFormInput() {
        return this.workflowState && this.workflowState.state == RightShipWorkflowStateEnum.PendingFormInput;
    }

    attached() {
        super.attached();
    }

    detached() {
        super.detached();
    }

    @computedFrom('workflowState', 'workflowFlagUpdater')
    public get upcoming(): WorkflowAssignmentModel[] {
        if (!this.workflowState) {
            return [];

        }
        const currentStep = this.getCurrentStep();
        return Enumerable.from(this.workflowState.workflowAssignments)
            .orderByDescending(x => x.stepNumber).where(x => !x.isCurrentAssignment && currentStep != null && x.stepNumber > currentStep).toArray();
    }

    @computedFrom('workflowState', 'workflowFlagUpdater')
    public get current(): WorkflowAssignmentModel[] {
        if (!this.workflowState)
            return [];

        return Enumerable.from(this.workflowState.workflowAssignments)
            .orderByDescending(x => x.stepNumber).where(x => x.stepNumber == this.getCurrentStep()).toArray();
    }

    @computedFrom('workflowState')
    public get completed(): WorkflowAssignmentModel[] {
        if (!this.workflowState)
            return [];
        const currentStep = this.getCurrentStep();
        return Enumerable.from(this.workflowState.workflowAssignments)
            .orderByDescending(x => new Date(x.closedDate))
            .where(x => !x.isCurrentAssignment && x.closedDate != null && (currentStep == null || x.stepNumber < currentStep))
            .toArray();
    }

    constructor(workflowAssignmentsService: WorkflowAssignmentsService,
        userClientService: UserClientService,
        eventAggregator: EventAggregator,
        dialogClientService: DialogClientService,
        notificationClientService: NotificationClientService,
        i18N: I18N,
        userClaimService: UserClaimsService,
        private questionCommentsService: RightShipOnboardingQuestionCommentService) {
        super(workflowAssignmentsService,
            userClientService,
            eventAggregator,
            dialogClientService,
            notificationClientService,
            i18N);

        this.canReopen = userClaimService.hasPermission(Claims.CanReopen);
        this.canCancel = userClaimService.hasPermission(Claims.CanCancel);
        this.canInvalidate = userClaimService.hasPermission(Claims.CanInvalidate);
    }

    @computedFrom("workflowState", "workflowForEntity.workflowState.state")
    public get showCancel() {
        if (!this.workflowState)
            return false;

        let logic = !(this.workflowState.state == RightShipWorkflowStateEnum.Unacceptable || this.workflowState.state == RightShipWorkflowStateEnum.Cancelled);

        if (this.isInvalidate()) {
            logic = logic && this.canInvalidate && this.shouldShowInvalidate;
        }
        else {
            logic = logic && this.canCancel && this.shouldShowCancel;
        }

        return logic;
    }

    @computedFrom("workflowState", "workflowState.state")
    public get showReopen() {

        if (!this.workflowState || !this.workflowState.workflowAssignments ||
            this.workflowState.workflowAssignments.length == 0 || !this.canReopen || !this.shouldShowReopen)
            return false;

        return (
            this.workflowState.state == RightShipWorkflowStateEnum.Accepted ||
            this.workflowState.state == RightShipWorkflowStateEnum.Unacceptable ||
            this.workflowState.state == RightShipWorkflowStateEnum.Cancelled ||
            this.workflowState.state == RightShipWorkflowStateEnum.Completed);
    }

    @computedFrom("workflowState", "workflowState.state")
    public get cancelButtonText() {

        if (this.isInvalidate()) {
            return 'INVALIDATE';
        }
        else {
            return 'CANCEL WORKFLOW';
        }
    }

    public getViewModel(internalName: string) {
        if (internalName) {
            require('views/plugins/workflow-activities/' + internalName + '/activity-assignment.ts');
            require('views/plugins/workflow-activities/' + internalName + '/activity-assignment.html');
            return `views/plugins/workflow-activities/${internalName}/activity-assignment`;
        }
    }

    private isInvalidate() {
        return (this.workflowState && (this.workflowState.state == RightShipWorkflowStateEnum.Accepted || this.workflowState.state == RightShipWorkflowStateEnum.Completed));
    }

    public cancelWorkflow() {

        if (this.isCancelling) return;

        this.isCancelling = true;

        return new Promise<void>((resolve, reject) => {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.CancelWorkflowDialog, {
                saveFunc: (comment: string) => {
                    const assignment = this.workflowState.workflowAssignments[this.workflowState.workflowAssignments.length - 1];

                    const meta = assignment.meta ? JSON.parse(assignment.meta) : {};
                    meta.reopened = true;
                    assignment.meta = JSON.stringify(meta);

                    this.workflowAssignmentsService.executeAction(assignment.workflowStateId,
                        assignment.internalName,
                        this.isInvalidate() ? 'invalidate' : 'cancel',
                        assignment.workflowAssignmentId,
                        null,
                        comment,
                        assignment.meta).then(response => {
                            this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
                            this.isCancelling = false;
                            resolve();
                        }).catch(() => {
                            this.notificationClientService.show("Error Cancelling", "", NotificationType.Error);
                            this.isCancelling = false;
                            reject();
                        });
                },
                cancelFunc: () => {
                    this.isCancelling = false;
                    resolve();
                },
                invalidate: this.workflowState.state == RightShipWorkflowStateEnum.Accepted || this.workflowState.state == RightShipWorkflowStateEnum.Completed
            }, true, false, false, 'right');
        });
    }



    public reload = (workflowState: WorkflowStateModel, canTakeOwnership: boolean, canReassign: boolean) => {
        if (!workflowState) {
            this.loadWorkflow();
        } else {
            this.workflowState = workflowState;
            this.canTakeOwnership = canTakeOwnership;
            this.canReassign = canReassign;

            if (this.workflowState.state == RightShipWorkflowStateEnum.Accepted || this.workflowState.state == RightShipWorkflowStateEnum.Completed) {
                this.eventAggregator.publish(Messages.workflowCompleted, this.workflowState);
            }
        }

        if(this.current.length == 0 && this.upcoming.length == 0)
        {
            this.questionCommentsService.showQuestionComments = false;
        }
    }

    showNextSteps() {
        this.isShowNextSteps = !this.isShowNextSteps;
    }

    public reopenWorkflow = () => {

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ReopenWorkflowDialog, {
            reopenWorkflow: (remarks: string) => {
                return new Promise<void>((resolve, reject) => {

                    const assignment = this.workflowState.workflowAssignments[this.workflowState.workflowAssignments.length - 1];

                    const meta = assignment.meta ? JSON.parse(assignment.meta) : {};
                    meta.reopened = true;
                    meta.vettingContactEmails = [];
                    meta.vettingContactMessage = '';
                    assignment.meta = JSON.stringify(meta);

                    this.workflowAssignmentsService.executeAction(assignment.workflowStateId,
                        assignment.internalName,
                        'reopen',
                        assignment.workflowAssignmentId,
                        null,
                        remarks,
                        assignment.meta).then(response => {
                            this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
                            resolve();
                        }).catch(() => {
                            reject()
                        });
                });
            }
        }, true, false, false, 'right');


    }



    public takeOwnership() {
        this.workflowAssignmentsService.takeOwnership(this.currentAssignment.workflowAssignmentId).then(() => {
            this.reload(null, false, false);
            this.notificationClientService.show(this.i18N.tr('ownership-taken-text'), this.i18N.tr('workflow-has-been-assigned-to-you-text'), NotificationType.Success);
            this.updateWorkflowAssignmentsRS();
        }).catch(() => {
            this.notificationClientService.show(this.i18N.tr('ownership-has-been-taken-text'), this.i18N.tr('workflow-activity-has-not-been-assigned-to-you-text'), NotificationType.Error);
        });
    }

    private updateWorkflowAssignmentsRS() {
        if (this.workflowFlagUpdater) {
            this.workflowFlagUpdater = null;
        } else {
            this.workflowFlagUpdater = {};
        }
    }

    public chooseIcon(item: any) {
        if (this.workflowState.state == RightShipWorkflowStateEnum.Cancelled)
            return 'cross';
        else
            return item.isAccepted == true ? 'tick' : 'cross';
    }

    public applyIconStyles(item: any) {
        if (this.workflowState.state == RightShipWorkflowStateEnum.Cancelled)
            return 'rejected';
        else
            return item.isAccepted == true ? 'accepted' : 'rejected'
    }
}
